import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ProviderSearchContext from "../../../../../context/ProviderSearchContext";
import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";

import useProviderDetailPermissions from "./useProviderDetailPermissions";

function normalizeProviderDetail(detail = {}) {
  const normalizedAddress = detail?.address
    ? {
        ...detail.address,
        raw: detail.address.raw ?? detail.address.formatted ?? "",
      }
    : null;

  return {
    ...detail,
    pk: detail?.pk ?? detail?.id ?? null,
    id: detail?.id ?? detail?.pk ?? null,
    address: normalizedAddress,
    in_network: detail?.in_network ?? detail?.inNetwork ?? false,
    receiving_clients: detail?.receiving_clients ?? detail?.receivingClients ?? false,
    provider_types: detail?.provider_types ?? detail?.providerTypes ?? [],
  };
}

export default function useProviderDetail(providerDetail = {}, isEnabled = true) {
  const navigate = useNavigate();
  const { setProvider, setActiveProviders } = useContext(ProviderSearchContext);
  const currentUserService = useService("current-user-service");
  const providerRepository = useService("repositories/provider-repository");
  const providerId = providerDetail?.pk || providerDetail?.id;
  const loggedInUser = currentUserService?.user;
  const isProviderUser = loggedInUser?.entity?.entity_type_label === "Provider";
  const loggedInUserId = loggedInUser?.pk;

  const [activeProvObj, setActiveProvObj] = useState({});
  const [providerInformation, setProviderInformation] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [parentTrigger, setParentTrigger] = useState(false);

  const permissionState = useProviderDetailPermissions({
    activeProvObj,
    providerId,
  });

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (!providerId) {
      setProviderInformation({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    providerRepository
      .findRecord(providerId, { noCache: true })
      .then((response) => {
        setProviderInformation(normalizeProviderDetail(response || {}));
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        getModalHandler().openFailureModal(error);
      });
  }, [isEnabled, parentTrigger, providerId, providerRepository]);

  useEffect(() => {
    if (!isProviderUser) {
      setActiveProvObj({});
      return;
    }

    providerRepository
      .getManagedProvider(loggedInUserId)
      .then((response) => {
        const mappedProviders = (response?.results || []).reduce(
          (acc, item) => ({ ...acc, [item.pk]: item.pk }),
          {}
        );

        setActiveProvObj(mappedProviders);
      })
      .catch(() => {
        setActiveProvObj({});
      });
  }, [isProviderUser, loggedInUserId, providerRepository]);

  const handleViewAllClients = () => {
    setProvider([providerInformation.pk]);
    setActiveProviders([providerInformation.name]);
    navigate("/provider-client-search");
  };

  return {
    activeProvObj,
    handleViewAllClients,
    isLoading,
    parentTrigger,
    permissionState,
    providerInformation,
    setProviderInformation,
    setParentTrigger,
  };
}
